import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { usePortfolio } from "../context/PortfolioContext";
import { useAuth } from "../context/AuthContext";
import { fmtINR, fmtPct, fmtUnits, shortDate, longDate } from "../lib/format";
import { api } from "../lib/api";
import { downloadZip, stampedZip, stampedName } from "../lib/csv";
import { useToast } from "../hooks/use-toast";
import ExportButtons from "../components/ExportButtons";
import StatCard from "../components/StatCard";
import DriftBanner from "../components/DriftBanner";
import {
  LeaderboardRibbon,
  CapitalVelocity,
  PoolMetrics,
  NavAttribution,
  VintageChart,
  CompositionDonut,
  RecentActivityFeed,
  TopMovers,
  MemberChipStrip,
} from "../components/dashboard/DashboardWidgets";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Layers,
  TrendingUp,
  Percent,
  Loader2,
  Download,
  Sparkles,
  Archive,
} from "lucide-react";
import { Button } from "../components/ui/button";

function Stat(props) {
  return <StatCard {...props} />;
}

const NAV_RANGES = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1Y", days: 365 },
  { key: "ALL", days: 99999 },
];

function filterNavByRange(history, days) {
  if (!history || !history.length) return [];
  if (days >= 99999) return history;
  const latest = new Date(history[history.length - 1].date);
  const cutoff = new Date(latest);
  cutoff.setDate(cutoff.getDate() - days);
  return history.filter((d) => new Date(d.date) >= cutoff);
}

export default function Dashboard() {
  const { user } = useAuth();
  const { summary, members, transactions, navHistory, loading, err } = usePortfolio();
  const { toast } = useToast();
  const [navRange, setNavRange] = useState("ALL");
  const [zipBusy, setZipBusy] = useState(false);
  const [recon, setRecon] = useState(null);

  // Lightweight poll of the reconciliation status — only the small payload,
  // not the full account list. Re-fetches when the portfolio data changes.
  useEffect(() => {
    let cancelled = false;
    api
      .ledgerReconciliation()
      .then((d) => {
        if (cancelled) return;
        setRecon({
          reconciliations: d?.reconciliations || [],
          allBalanced: !!d?.allBalanced,
        });
      })
      .catch(() => {
        // silent — drift banner just won't show
      });
    return () => {
      cancelled = true;
    };
  }, [summary?.lastUpdated, transactions?.length]);

  const chartData = useMemo(
    () => filterNavByRange(navHistory, NAV_RANGES.find((r) => r.key === navRange).days),
    [navRange, navHistory]
  );

  const dashboardExport = useMemo(() => {
    const p = summary || {};
    const headers = ["Metric", "Value"];
    const rows = [
      ["As of", p.lastNavDate || (p.lastUpdated || "").slice(0, 10)],
      ["Current NAV", p.currentNAV],
      ["Portfolio Value (Capital + Bonus)", p.currentValue],
      ["Total Capital Value", p.totalCapitalValue],
      ["Total Bonus Value", p.totalBonusValue],
      ["Total Capital (Invested)", p.totalCapital],
      ["Total Bonus Capital", p.totalBonusCapital],
      ["Total Subscription Units", p.totalUnits],
      ["Total Bonus Units", p.totalBonusUnits],
      ["Total All Units", p.totalAllUnits],
      ["Total Gain/Loss", p.totalGainLoss],
      ["Portfolio Return %", (p.portfolioReturnPct || 0).toFixed(4)],
      ["MTM Primary", p.portfolioMTMPrimary],
      ["MTM Mutual Fund", p.portfolioMTMMutualFund],
      ["Total Charges", p.totalCharges],
      ["Other Cr/Dr", p.otherCrDr],
      ["STCG (post-tax)", p.bookedProfitsSTCG?.postTax],
      ["LTCG (post-tax)", p.bookedProfitsLTCG?.postTax],
      ["Speculation (post-tax)", p.bookedProfitsSpeculation?.postTax],
      ["Dividend (post-tax)", p.dividendReceived?.postTax],
      ["Non Equity (post-tax)", p.nonEquity?.postTax],
    ];
    return { headers, rows };
  }, [summary]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading portfolio…
        </div>
      </AppShell>
    );
  }
  if (err || !summary) {
    return (
      <AppShell>
        <div className="text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-4 py-3 text-sm">
          {err || "No data available."}
        </div>
      </AppShell>
    );
  }

  const p = summary;
  const me = members.find((m) => m.id === user?.memberId);
  const isAdmin = user?.role === "admin";
  const hasBonus = me && ((me.bonusUnits || 0) > 0 || (me.bonusCapital || 0) > 0);
  const overallVal = me?.overallCurrentValue != null ? me.overallCurrentValue : me?.currentValue;
  const overallGain = me?.overallGainLoss != null ? me.overallGainLoss : me?.gainLoss;
  const overallPct = me?.overallGainPct != null ? me.overallGainPct : me?.gainLossPct;
  const overallUnits = me?.overallUnits != null ? me.overallUnits : me?.units;

  const handleDownloadAllZip = async () => {
    setZipBusy(true);
    try {
      // Try fetching live holdings (Portfolio page data); skip on failure
      let holdings = null;
      try {
        holdings = await api.holdings();
      } catch (_) {
        // no portfolio holdings uploaded yet — skip
      }

      const sheets = [];

      // 1. Dashboard summary
      sheets.push({
        name: "01_dashboard_summary.csv",
        headers: ["Metric", "Value"],
        rows: [
          ["As of", p.lastNavDate || (p.lastUpdated || "").slice(0, 10)],
          ["Current NAV", p.currentNAV],
          ["Portfolio Value", p.currentValue],
          ["Total Capital Value", p.totalCapitalValue],
          ["Total Bonus Value", p.totalBonusValue],
          ["Total Capital (Invested)", p.totalCapital],
          ["Total Bonus Capital", p.totalBonusCapital],
          ["Total Subscription Units", p.totalUnits],
          ["Total Bonus Units", p.totalBonusUnits],
          ["Total All Units", p.totalAllUnits],
          ["Total Gain/Loss", p.totalGainLoss],
          ["Portfolio Return %", (p.portfolioReturnPct || 0).toFixed(4)],
        ],
      });

      // 2. Members (with capital + bonus + overall)
      sheets.push({
        name: "02_members.csv",
        headers: [
          "Member", "Capital Invested", "Capital Units", "Avg NAV",
          "Capital Current Value", "Capital Gain/Loss", "Capital %",
          "Bonus Units", "Bonus Capital", "Bonus Value", "Bonus Gain", "Bonus %",
          "Total Units", "Overall Current Value", "Overall Gain/Loss", "Overall %", "Holding %",
        ],
        rows: members.map((m) => [
          m.name, m.capitalInvested, m.units, m.avgNAV,
          m.currentValue, m.gainLoss, (m.gainLossPct || 0).toFixed(2),
          m.bonusUnits || 0, m.bonusCapital || 0, m.bonusValue || 0, m.bonusGainLoss || 0, (m.bonusGainPct || 0).toFixed(2),
          m.overallUnits || m.units, m.overallCurrentValue || m.currentValue, m.overallGainLoss || m.gainLoss,
          (m.overallGainPct != null ? m.overallGainPct : m.gainLossPct || 0).toFixed(2),
          (m.holdingPct || 0).toFixed(2),
        ]),
      });

      // 3. Cashflows (sub + red)
      const cashflows = transactions.filter((t) => t.type !== "bonus");
      sheets.push({
        name: "03_cashflows.csv",
        headers: ["Date", "Member", "Type", "NAV", "Units", "Amount", "AMC Fee", "Details"],
        rows: cashflows.map((c) => {
          const inflow = (c.fundsCr || 0) > 0;
          return [
            c.date, c.name,
            inflow ? "Subscription" : "Redemption",
            c.nav,
            inflow ? c.unitsCr : c.unitsDr,
            inflow ? c.fundsCr : -c.fundsDr,
            c.amcFee || 0,
            c.details || "",
          ];
        }),
      });

      // 4. Bonus
      const bonusTxs = transactions.filter((t) => t.type === "bonus");
      sheets.push({
        name: "04_bonus.csv",
        headers: ["Date", "Member", "Bonus NAV", "Bonus Units", "Bonus Capital", "Current NAV", "Current Value", "Bonus Gain", "Bonus Gain %", "Details"],
        rows: bonusTxs.map((c) => {
          const units = c.unitsCr || 0;
          const cap = c.fundsCr || 0;
          const cv = units * (p.currentNAV || 0);
          const g = cv - cap;
          const gp = cap > 0 ? (g / cap) * 100 : 0;
          return [c.date, c.name, c.nav, units, cap, p.currentNAV, cv, g, gp.toFixed(2), c.details || ""];
        }),
      });

      // 5. NAV History
      sheets.push({
        name: "05_nav_history.csv",
        headers: ["Date", "NAV", "Member", "Type", "Funds", "Units"],
        rows: [...navHistory]
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map((d) => [d.date, d.nav, d.memberName || "", d.type || "", d.funds || "", d.units || ""]),
      });

      // 6. Portfolio holdings (Zerodha-style)
      if (holdings?.holdings?.length) {
        sheets.push({
          name: "06_portfolio_holdings.csv",
          headers: ["Symbol", "ISIN", "Instrument Type", "Category", "Quantity", "Avg Price", "Last Price", "Invested", "Current", "P&L", "P&L %"],
          rows: holdings.holdings.map((h) => {
            const invested = (h.avgPrice || 0) * (h.quantity || 0);
            const current = (h.lastPrice || 0) * (h.quantity || 0);
            const pnl = current - invested;
            const pct = invested > 0 ? (pnl / invested) * 100 : 0;
            return [h.symbol, h.isin || "", h.instrumentType || "", h.category, h.quantity, h.avgPrice, h.lastPrice, invested, current, pnl, pct.toFixed(2)];
          }),
        });
      }

      await downloadZip(stampedZip("daasmin_export"), sheets);
      toast({ title: "Export complete", description: `${sheets.length} CSVs bundled into ZIP` });
    } catch (e) {
      toast({ title: "Export failed", description: e.message || "Could not generate ZIP" });
    } finally {
      setZipBusy(false);
    }
  };

  return (
    <AppShell>
      {recon && !recon.allBalanced && (
        <DriftBanner
          reconciliations={recon.reconciliations}
          allBalanced={recon.allBalanced}
        />
      )}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/80 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            Dashboard · Realtime
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">
            Welcome, <span className="brand-gradient">{user?.name?.split(" ")[0]}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-right hidden md:block px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-800/70">
            <div className="text-[9.5px] text-slate-500 uppercase tracking-[0.18em] font-mono">As of</div>
            <div className="text-sm text-slate-200 font-mono tabular-nums">
              {p.lastNavDate ? longDate(p.lastNavDate) : longDate(p.lastUpdated)}
            </div>
          </div>
          {isAdmin && (
            <>
              <Button
                onClick={handleDownloadAllZip}
                disabled={zipBusy}
                data-testid="dashboard-download-zip-btn"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold disabled:opacity-50"
              >
                <Archive size={14} className="mr-2" />
                {zipBusy ? "Bundling…" : "Download All (ZIP)"}
              </Button>
            </>
          )}
          <ExportButtons
            testIdPrefix="dashboard-export"
            csv={{ filename: stampedName("dashboard_summary"), ...dashboardExport }}
            pdf={{
              title: "Dashboard summary",
              subtitle: p.lastNavDate ? `As of ${longDate(p.lastNavDate)}` : undefined,
              filename: "dashboard_summary",
              columns: dashboardExport.headers,
              rows: dashboardExport.rows,
            }}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
        <Stat
          label="Current NAV"
          value={`₹${p.currentNAV.toFixed(4)}`}
          sub={fmtPct(p.portfolioReturnPct)}
          positive={p.portfolioReturnPct >= 0}
          icon={TrendingUp}
        />
        <Stat
          label="Portfolio Value"
          value={fmtINR(p.currentValue, { compact: true })}
          sub={`${fmtUnits(p.totalAllUnits || p.totalUnits)} units × NAV`}
          icon={Wallet}
        />
        <Stat
          label="Total Capital"
          value={fmtINR(p.totalCapital, { compact: true })}
          sub={`${fmtUnits(p.totalUnits)} subscription units`}
          icon={Layers}
        />
        <Stat
          label="Total Bonus"
          value={fmtINR(p.totalBonusCapital || 0, { compact: true })}
          sub={
            p.totalBonusUnits
              ? `${fmtUnits(p.totalBonusUnits)} bonus units · worth ${fmtINR(p.totalBonusValue || 0, { compact: true })}`
              : "No bonus issued yet"
          }
          icon={Sparkles}
        />
        <Stat
          label="Total Gain/Loss"
          value={fmtINR(p.totalGainLoss, { compact: true })}
          sub={fmtPct(p.portfolioReturnPct)}
          positive={p.totalGainLoss >= 0}
          icon={Percent}
        />
      </div>

      {/* Leaderboard ribbon */}
      <LeaderboardRibbon members={members} transactions={transactions} />

      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-semibold text-white">NAV Trend</div>
            <div className="text-xs text-slate-500">
              {chartData.length > 0
                ? `${chartData.length} data points · ${shortDate(chartData[0].date)} → ${shortDate(chartData[chartData.length - 1].date)}`
                : "No data in range"}
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="dashboard-nav-range">
            {NAV_RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`dashboard-nav-range-${r.key}`}
                onClick={() => setNavRange(r.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                  navRange === r.key
                    ? "bg-emerald-500 text-slate-950 border-emerald-500"
                    : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                {r.key}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3 h-52 md:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
              <defs>
                <linearGradient id="navFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                stroke="#475569"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
              />
              <YAxis
                stroke="#475569"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                domain={["dataMin - 1", "dataMax + 1"]}
                tickFormatter={(v) => v.toFixed(1)}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#e2e8f0",
                }}
                formatter={(v) => [`₹${Number(v).toFixed(4)}`, "NAV"]}
                labelFormatter={shortDate}
              />
              <Area type="monotone" dataKey="nav" stroke="#10b981" strokeWidth={2} fill="url(#navFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick metrics row: capital velocity + pool maturity + attribution */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <CapitalVelocity transactions={transactions} />
        <PoolMetrics members={members} transactions={transactions} summary={summary} />
        <NavAttribution members={members} summary={summary} />
      </div>

      {/* Vintage chart + composition donut */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <VintageChart transactions={transactions} />
        </div>
        <CompositionDonut members={members} />
      </div>

      {/* Recent activity + top movers */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentActivityFeed transactions={transactions} members={members} />
        <TopMovers members={members} />
      </div>

      {/* Member chip strip */}
      <div className="mt-6">
        <MemberChipStrip members={members} />
      </div>

      {user?.role === "admin" && (
        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-white">Portfolio Snapshot</div>
              <div className="text-xs text-slate-500">Breakdown and booked profits (admin view)</div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
              Admin
            </span>
          </div>
          <dl className="mt-4 grid md:grid-cols-2 gap-x-8 text-sm">
            <Row k="Total Capital" v={fmtINR(p.totalCapital)} />
            <Row k="Total Units Issued" v={fmtUnits(p.totalUnits)} />
            <Row k="MTM (Primary)" v={fmtINR(p.portfolioMTMPrimary)} positive={p.portfolioMTMPrimary >= 0} />
            <Row k="MTM (Mutual Fund)" v={fmtINR(p.portfolioMTMMutualFund)} positive={p.portfolioMTMMutualFund >= 0} />
            <Row k="Total Charges" v={fmtINR(p.totalCharges)} />
            <Row k="Other Cr/Dr" v={fmtINR(p.otherCrDr)} />
            <Row k="STCG (post-tax 20%)" v={fmtINR(p.bookedProfitsSTCG.postTax)} />
            <Row k="LTCG (post-tax 15%)" v={fmtINR(p.bookedProfitsLTCG.postTax)} />
            <Row k="Speculation (post-tax 30%)" v={fmtINR(p.bookedProfitsSpeculation.postTax)} />
            <Row k="Dividend (post-tax 30%)" v={fmtINR(p.dividendReceived.postTax)} />
            <Row k="Non Equity (post-tax 20%)" v={fmtINR(p.nonEquity.postTax)} />
          </dl>
        </div>
      )}

      {me && (me.units > 0 || hasBonus) && (
        <div className="mt-6 bg-slate-900 border-2 border-emerald-500/40 rounded-xl p-4 md:p-5" data-testid="dashboard-overall-card">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] text-emerald-300 uppercase tracking-widest flex items-center gap-2">
                Overall (Capital{hasBonus ? " + Bonus" : ""})
              </div>
              <div className="mt-1 text-lg md:text-xl font-semibold text-white">{me.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {fmtUnits(overallUnits || 0)} total units
                {hasBonus && (
                  <span className="text-amber-300"> · incl. {fmtUnits(me.bonusUnits)} bonus</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl md:text-3xl font-semibold text-white tracking-tight">
                {fmtINR(overallVal || 0)}
              </div>
              <div
                className={`mt-1 inline-flex items-center gap-1 text-sm ${
                  (overallGain || 0) >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {(overallGain || 0) >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {fmtINR(overallGain || 0)} ({fmtPct(overallPct || 0)})
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-slate-800 pt-4">
            <MiniStat label="Capital Invested" value={fmtINR(me.capitalInvested, { compact: true })} />
            <MiniStat label="Capital Value" value={fmtINR(me.currentValue, { compact: true })} />
            <MiniStat label="Avg Cost NAV" value={`₹${me.avgNAV.toFixed(4)}`} />
            <MiniStat label="Current NAV" value={`₹${(p.currentNAV || 0).toFixed(4)}`} />
          </div>

          {hasBonus && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-amber-500/20 pt-3">
              <MiniStat
                label={<span className="flex items-center gap-1 text-amber-300"><Sparkles size={10} /> Bonus Capital</span>}
                value={fmtINR(me.bonusCapital, { compact: true })}
              />
              <MiniStat
                label={<span className="text-amber-300">Bonus Units</span>}
                value={fmtUnits(me.bonusUnits)}
              />
              <MiniStat
                label={<span className="text-amber-300">Bonus Value</span>}
                value={fmtINR(me.bonusValue, { compact: true })}
              />
              <MiniStat
                label={<span className="text-amber-300">Bonus Gain</span>}
                value={`${fmtINR(me.bonusGainLoss, { compact: true })} (${fmtPct(me.bonusGainPct)})`}
              />
            </div>
          )}
          <div className="mt-3 text-[10px] text-slate-500">
            Overall % computed against Capital Invested. {hasBonus && "Bonus units appreciate at the same NAV as capital."}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Row({ k, v, positive }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
      <dt className="text-slate-400">{k}</dt>
      <dd className={`font-medium ${positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : "text-white"}`}>{v}</dd>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase tracking-widest">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white">{value}</div>
    </div>
  );
}
